import _ from 'underscore';

import {assertAndContinue} from 'Asserts';
import {NucleotideType} from 'biojs/BioConstants';
import ModelTypeConstants, {FOLDER_ITEM_TYPES} from 'constants/ModelTypeConstants';
import {
  BATCH_SUPPORTED_TYPES,
  OligoAwareFolderItemType,
  REGISTRY_SUPPORTED_TYPES,
} from 'constants/RegistryConstants';
import {STORABLE_SCHEMA_TYPES, STORABLE_TYPES} from 'constants/SampleConstants';
import {SequenceSummary, SequenceType} from 'constants/SequenceConstants';
import {FileSummary} from 'folder-items/FileConstants';
import {PARTY_TYPES} from 'parties/PartyConstants';
import {BenchlingIconName, isValidBenchlingIcon} from 'ui-library/icons/BenchlingIcon';

const API_PREFIX_TO_TYPE: Record<string, ModelTypeConstants> = {
  agtskl: ModelTypeConstants.AGENT_SKILL,
  agtsklfl: ModelTypeConstants.AGENT_SKILL_FILE,
  aimdl: ModelTypeConstants.AI_MODEL,
  ana: ModelTypeConstants.ANALYSIS,
  anatmpl: ModelTypeConstants.ANALYSIS_TEMPLATE,
  assem: ModelTypeConstants.BULK_ASSEMBLY,
  assaysch: ModelTypeConstants.ASSAY_SCHEMA,
  aitf: ModelTypeConstants.AUTOMATION_INPUT_TRANSFORM_FILE,
  aof: ModelTypeConstants.AUTOMATION_OUTPUT_FILE,
  aopf: ModelTypeConstants.AUTOMATION_OUTPUT_TRANSFORM_FILE,
  axdash: ModelTypeConstants.ANALYTICS_DASHBOARD,
  axquery: ModelTypeConstants.ANALYTICS_QUERY,
  abfmt: ModelTypeConstants.ANTIBODY_FORMAT,
  app: ModelTypeConstants.BENCHLING_APP,
  bat: ModelTypeConstants.BATCH,
  batsch: ModelTypeConstants.BATCH_SCHEMA,
  bfi: ModelTypeConstants.BASIC_FOLDER_ITEM,
  blob: ModelTypeConstants.BLOB,
  box: ModelTypeConstants.BOX,
  boxsch: ModelTypeConstants.BOX_SCHEMA,
  chart: ModelTypeConstants.CHART,
  chtsess: ModelTypeConstants.CHAT_SESSION,
  con: ModelTypeConstants.CONTAINER,
  consch: ModelTypeConstants.CONTAINER_SCHEMA,
  cxn: ModelTypeConstants.CONNECTION,
  cxnsch: ModelTypeConstants.CONNECTION_SCHEMA,
  dataset: ModelTypeConstants.DATASET,
  dset: ModelTypeConstants.DATA_FRAME,
  doc: ModelTypeConstants.DOCUMENT,
  ent: ModelTypeConstants.ENTITY,
  eprt: ModelTypeConstants.ENTRY_PROTOCOL,
  etr: ModelTypeConstants.ENTRY,
  file: ModelTypeConstants.FILE,
  fmdef: ModelTypeConstants.FORM_DEFINITION,
  ingr: ModelTypeConstants.INGREDIENT,
  intrfc: ModelTypeConstants.SCHEMA_INTERFACE,
  jly: ModelTypeConstants.JELLYFISH,
  lbltmp: ModelTypeConstants.LABEL_TEMPLATE,
  lib: ModelTypeConstants.FOLDER,
  loc: ModelTypeConstants.LOCATION,
  locsch: ModelTypeConstants.LOCATION_SCHEMA,
  mcpconf: ModelTypeConstants.MCP_SERVER_CONFIG,
  mcpusr: ModelTypeConstants.MCP_SERVER_USER_CONFIG,
  mol: ModelTypeConstants.MOLECULE,
  mon: ModelTypeConstants.MONOMER,
  mxt: ModelTypeConstants.MIXTURE,
  mxtinstr: ModelTypeConstants.MIXTURE_INSTRUCTIONS,
  pardef: ModelTypeConstants.LIBRARY_PARAMETER_DEFINITION,
  pc: ModelTypeConstants.PROCEDURE,
  pcmei: ModelTypeConstants.PROCEDURE_METHOD_EXECUTION_INSTANCE,
  pcr: ModelTypeConstants.PROCEDURE_RUN,
  prp: ModelTypeConstants.PROCEDURE_RUN_PLAN,
  pcsei: ModelTypeConstants.PROCEDURE_STEP_EXECUTION_INSTANCE,
  plt: ModelTypeConstants.PLATE,
  pltsch: ModelTypeConstants.PLATE_SCHEMA,
  print: ModelTypeConstants.PRINTER,
  prs: ModelTypeConstants.PROCESS,
  prssch: ModelTypeConstants.PROCESS_SCHEMA,
  prstsch: ModelTypeConstants.PROCESS_TASK_SCHEMA,
  prsosch: ModelTypeConstants.PROCESS_OUTPUT_SCHEMA,
  prt: ModelTypeConstants.PROTOCOL,
  prtn: ModelTypeConstants.PROTEIN,
  prtnalnmnt: ModelTypeConstants.PROTEIN_ALIGNMENT,
  qnty: ModelTypeConstants.UNIT_TYPE,
  rqdf: ModelTypeConstants.REQUEST_V2_DEFINITION,
  rqsm: ModelTypeConstants.REQUEST_V2_SUBMISSION,
  req: ModelTypeConstants.REQUEST,
  reqtsksch: ModelTypeConstants.REQUEST_TASK_SCHEMA,
  run: ModelTypeConstants.ASSAY_RUN,
  rvw: ModelTypeConstants.REVIEW,
  rxn: ModelTypeConstants.REACTION,
  rlc: ModelTypeConstants.REVIEW_LIFECYCLE,
  sbtmpl: ModelTypeConstants.SUBTEMPLATE,
  scrfile: ModelTypeConstants.SCRATCH_FILE,
  scrplt: ModelTypeConstants.SCRATCH_PLATE,
  spltdsgn: ModelTypeConstants.PLATE_DESIGN_TEMPLATE,
  seq: ModelTypeConstants.SEQUENCE,
  seqanl: ModelTypeConstants.SEQUENCE_ANALYSIS,
  sfs: ModelTypeConstants.SCHEMA_FIELD_SELECTOR,
  slt: ModelTypeConstants.SALT,
  slvt: ModelTypeConstants.SOLVENT,
  sfso: ModelTypeConstants.SCHEMA_FIELD_SELECTOR_OPTION,
  simpletabledoc: ModelTypeConstants.SIMPLE_TABLE_DOC,
  smpldef: ModelTypeConstants.SAMPLE_DEFINITION,
  smpltyp: ModelTypeConstants.SAMPLE_TYPE,
  ss: ModelTypeConstants.SAVED_SEARCH,
  ssi: ModelTypeConstants.STATIC_SCHEMA_INTERFACE,
  stdy: ModelTypeConstants.STUDY,
  stdysch: ModelTypeConstants.STUDY_SCHEMA,
  stdydsgn: ModelTypeConstants.STUDY_DESIGN,
  src: ModelTypeConstants.SOURCE,
  stgcfg: ModelTypeConstants.STAGE_CONFIGURATION,
  stgentr: ModelTypeConstants.STAGE_ENTRY,
  tdef: ModelTypeConstants.TEST_DEFINITION,
  tord: ModelTypeConstants.TEST_ORDER,
  team: ModelTypeConstants.TEAM,
  tsf: ModelTypeConstants.SCHEMA_FIELD,
  tfia: ModelTypeConstants.TFI_ACCOUNT,
  tmpl: ModelTypeConstants.TEMPLATE,
  tmplcol: ModelTypeConstants.TEMPLATE_COLLECTION,
  tntcl: ModelTypeConstants.TENTACLE,
  ts: ModelTypeConstants.TAG_SCHEMA,
  unit: ModelTypeConstants.UNIT,
  vdrl: ModelTypeConstants.VALIDATION_RULE,
  wfout: ModelTypeConstants.PROCESS_OUTPUT,
  wftask: ModelTypeConstants.PROCESS_TASK,
  wfw: ModelTypeConstants.WORKFLOW,
  wfwtmpl: ModelTypeConstants.WORKFLOW_TEMPLATE,
  wkl: ModelTypeConstants.WORKLIST,
  wks: ModelTypeConstants.WORKSHEET,
};

const TYPE_TO_API_PREFIX = _.invert(API_PREFIX_TO_TYPE);

export function getAPIIDPrefix(apiId: string): string | null {
  const index = apiId.indexOf('_');
  if (index !== -1) {
    return apiId.slice(0, index);
  } else {
    return null;
  }
}

function apiIdToMaybeType(apiId: string, logIfMissingType: boolean = false): ModelTypeConstants | null {
  if (!assertAndContinue(apiId != null, 'API ID may not be null')) {
    return null!;
  }
  if (
    !assertAndContinue(
      typeof apiId === 'string',
      'API ID passed in was not a string, and this method will return null. ' +
        'In general we should avoid using non-string (legacy) API IDs with this module.'
    )
  ) {
    return null;
  }
  const prefix = getAPIIDPrefix(apiId)!;
  const type = API_PREFIX_TO_TYPE[prefix];

  if (logIfMissingType) {
    // If hitting this in tests for an autogenerated node where it does not
    // include a prefix in its autogenerated id, take a look at the
    // generateIDOrUndefined function in
    // client/graphql-client/schema-link/defaultMocks.ts
    assertAndContinue(type != null, `APIUtil: Prefix ${prefix} not found in lookup data`);
  }

  return type || null;
}

function apiIdToType(apiId: string): ModelTypeConstants {
  return apiIdToMaybeType(apiId, true)!;
}

export function apiIdToIconName(apiId: string): BenchlingIconName {
  const type = apiIdToType(apiId);
  if (isValidBenchlingIcon(type)) {
    return type;
  } else {
    assertAndContinue(
      false,
      `APIUtil: Type '${type}' is not a valid icon name. Suggested fix: add an alias in IconAliases.ts`
    );
    return 'notdef';
  }
}

function withPrefixIfMissing(id: string, modelType: string): string {
  if (id.includes('_')) {
    return id;
  }
  return APIUtil.prefixApiId(modelType, id);
}

export function fileToOligoAwareFolderItemType(file: FileSummary): OligoAwareFolderItemType {
  const folderItemType = apiIdToType(file.id);
  if (folderItemType === ModelTypeConstants.SEQUENCE) {
    const sequence = file as SequenceSummary;
    if (sequence.sequence_type === SequenceType.DOUBLE_STRANDED) {
      return ModelTypeConstants.SEQUENCE;
    } else if (sequence.sequence_type === SequenceType.OLIGO) {
      return sequence.nucleotideType === NucleotideType.DNA
        ? ModelTypeConstants.DNA_OLIGO
        : ModelTypeConstants.RNA_OLIGO;
    } else if (sequence.sequence_type === SequenceType.SINGLE_STRANDED) {
      return ModelTypeConstants.RNA_SEQUENCE;
    } else {
      assertAndContinue(false, `Unrecognized sequence type: ${sequence.type}`);
      return ModelTypeConstants.SEQUENCE;
    }
  } else {
    return folderItemType as OligoAwareFolderItemType;
  }
}

function isEntryOrSubOrWorkflowTemplateFileType(fileType: string): boolean {
  return [
    ModelTypeConstants.TEMPLATE,
    ModelTypeConstants.STAGE_CONFIGURATION,
    ModelTypeConstants.SUBTEMPLATE,
  ].includes(fileType as ModelTypeConstants);
}

const APIUtil = {
  apiIdToMaybeType,
  apiIdToType,
  typeToApiPrefix(type: string): string {
    return TYPE_TO_API_PREFIX[type];
  },
  prefixApiId(type: string, unprefixedApiId: string): string {
    return `${TYPE_TO_API_PREFIX[type]}_${unprefixedApiId}`;
  },
  unprefixApiId(apiId: string): string {
    return apiId.split('_')[1];
  },
  isAutomationTransformFile(apiId: string): boolean {
    const type = apiIdToMaybeType(apiId);
    return (
      type != null &&
      [
        ModelTypeConstants.AUTOMATION_INPUT_TRANSFORM_FILE,
        ModelTypeConstants.AUTOMATION_OUTPUT_TRANSFORM_FILE,
      ].includes(type)
    );
  },
  isAutomationOutputFile(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.AUTOMATION_OUTPUT_FILE;
  },
  isFolder(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.FOLDER;
  },
  isEntry(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.ENTRY;
  },
  isDocument(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.DOCUMENT;
  },
  isStageEntry(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.STAGE_ENTRY;
  },
  isBaseEntry(apiId: string): boolean {
    return this.isDocument(apiId) || this.isEntry(apiId) || this.isStageEntry(apiId);
  },
  isBasicFolderItem(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.BASIC_FOLDER_ITEM;
  },
  isEntryTemplate(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.TEMPLATE;
  },
  isStageEntryTemplate(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.STAGE_CONFIGURATION;
  },
  isBiologicalEntity(apiId: string): boolean {
    const type = apiIdToMaybeType(apiId);
    return type != null && (REGISTRY_SUPPORTED_TYPES as Array<string>).includes(type);
  },
  isNucleotideSequence(apiId: string): boolean {
    // cannot distinguish between sequence and oligo since they have the same id prefix
    return apiIdToMaybeType(apiId) === ModelTypeConstants.SEQUENCE;
  },
  isAssaySchema(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.ASSAY_SCHEMA;
  },
  isMixtureInstructions(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.MIXTURE_INSTRUCTIONS;
  },
  isConnection(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.CONNECTION;
  },
  isChart(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.CHART;
  },
  isChatSession(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.CHAT_SESSION;
  },
  isDataFrame(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.DATA_FRAME;
  },
  isDataset(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.DATASET;
  },
  isScratchFile(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.SCRATCH_FILE;
  },
  isScratchPlate(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.SCRATCH_PLATE;
  },
  isFile(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.FILE;
  },
  isProcedure(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.PROCEDURE;
  },
  isProcedureMethodExecutionInstance(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.PROCEDURE_METHOD_EXECUTION_INSTANCE;
  },
  isProcedureRun(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.PROCEDURE_RUN;
  },
  isProcessManagementItem(apiId: string): boolean {
    return this.isProcess(apiId) || this.isProcessOutput(apiId) || this.isProcessTask(apiId);
  },
  isProcess(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.PROCESS;
  },
  isProcessOutput(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.PROCESS_OUTPUT;
  },
  isProcessTask(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.PROCESS_TASK;
  },
  isProcessSchema(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.PROCESS_SCHEMA;
  },
  isProcessTaskSchema(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.PROCESS_TASK_SCHEMA;
  },
  isProcessOutputSchema(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.PROCESS_OUTPUT_SCHEMA;
  },
  isProtocol(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.PROTOCOL;
  },
  isProtein(apiId: string): boolean {
    // Protein refers to the legacy definition which is an AA sequence.
    // The new protein product uses the ANTIBODY model type, which cannot be identified by API ID alone.
    return apiIdToMaybeType(apiId) === ModelTypeConstants.PROTEIN;
  },
  isContainer(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.CONTAINER;
  },
  isContainerSchema(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.CONTAINER_SCHEMA;
  },
  isStorable(apiId: string): boolean {
    const type = apiIdToMaybeType(apiId);
    return type != null && (STORABLE_TYPES as unknown as Array<string>).includes(type);
  },
  isStudy(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.STUDY;
  },
  isStudySchema(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.STUDY_SCHEMA;
  },
  isBox(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.BOX;
  },
  isBoxSchema(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.BOX_SCHEMA;
  },
  isLocation(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.LOCATION;
  },
  isLocationSchema(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.LOCATION_SCHEMA;
  },
  isPlate(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.PLATE;
  },
  isPlateSchema(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.PLATE_SCHEMA;
  },
  isTagSchema(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.TAG_SCHEMA;
  },
  isSchemaInterface(apiId: string, includeStaticInterfaces: boolean = false): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.SCHEMA_INTERFACE;
  },
  isStaticSchemaInterface(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.STATIC_SCHEMA_INTERFACE;
  },
  isStorableSchema(apiId: string): boolean {
    const type = apiIdToMaybeType(apiId);
    return type != null && (STORABLE_SCHEMA_TYPES as unknown as Array<string>).includes(type);
  },
  // Return true if an APIIdentified item can reside in a folder
  // (e.g. folder, folder item, not stage entries)
  isFolderContainable(apiId: string): boolean {
    const type = apiIdToMaybeType(apiId);
    if (type == null) {
      return false;
    }
    return type === ModelTypeConstants.FOLDER || FOLDER_ITEM_TYPES.includes(type);
  },
  isFolderItem(apiId: string): boolean {
    const type = apiIdToMaybeType(apiId);
    return type != null && FOLDER_ITEM_TYPES.includes(type);
  },
  isBatch(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.BATCH;
  },
  isBatchSchema(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.BATCH_SCHEMA;
  },
  isBulkAssembly(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.BULK_ASSEMBLY;
  },
  hasAuthors(apiId: string): boolean {
    // TODO(BNCH-66819): Replace this manual implementation with "does the `apiId`'s corresponding Domain
    // Graph type implement the Has Authors interface". We could use front-end Core Object Introspection for
    // this.
    if (APIUtil.isDataset(apiId)) {
      return false;
    }
    return (
      APIUtil.isStageEntry(apiId) ||
      (APIUtil.isFolderItem(apiId) &&
        !APIUtil.isBulkAssembly(apiId) &&
        !APIUtil.isFile(apiId) &&
        !APIUtil.isTemplateCollectible(apiId))
    );
  },
  isNotebookEntryOrTemplate(apiId: string): boolean {
    return APIUtil.isEntry(apiId) || APIUtil.isEntryTemplate(apiId);
  },
  isMixture(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.MIXTURE;
  },
  isWorklist(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.WORKLIST;
  },
  isWorksheet(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.WORKSHEET;
  },
  isEntryOrSubOrWorkflowTemplate(apiId: string): boolean {
    return isEntryOrSubOrWorkflowTemplateFileType(apiIdToType(apiId));
  },
  isEntryOrSubOrWorkflowTemplateFileType,
  // TODO(BNCH-108866): Remove isExecutionDocument and isConfigurationDocument
  isExecutionDocument(apiId: string): boolean {
    return (
      [ModelTypeConstants.ENTRY, ModelTypeConstants.DOCUMENT, ModelTypeConstants.STAGE_ENTRY].find(
        (modelType) => APIUtil.apiIdToMaybeType(apiId) === modelType
      ) != null
    );
  },
  isConfigurationDocument(apiId: string): boolean {
    return (
      [
        ModelTypeConstants.TEMPLATE,
        ModelTypeConstants.STAGE_CONFIGURATION,
        ModelTypeConstants.SUBTEMPLATE,
      ].find((modelType) => APIUtil.apiIdToMaybeType(apiId) === modelType) != null
    );
  },
  isSubtemplate(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.SUBTEMPLATE;
  },

  getTemplateCollectibleTypes(): Array<ModelTypeConstants> {
    return [
      ModelTypeConstants.TEMPLATE,
      ModelTypeConstants.SUBTEMPLATE,
      ModelTypeConstants.FORM_DEFINITION,
      ModelTypeConstants.PROCEDURE,
      ModelTypeConstants.REQUEST_V2_DEFINITION,
      ModelTypeConstants.ANALYSIS_TEMPLATE,
      ModelTypeConstants.PLATE_DESIGN_TEMPLATE,
    ];
  },

  isTemplateCollectible(apiId: string): boolean {
    const type = apiIdToMaybeType(apiId);
    return type != null && this.getTemplateCollectibleTypes().includes(type);
  },
  isEntryOrStageEntry(apiId: string): boolean {
    const maybeType = apiIdToMaybeType(apiId);
    return (
      maybeType != null && [ModelTypeConstants.ENTRY, ModelTypeConstants.STAGE_ENTRY].includes(maybeType)
    );
  },
  isAnalysis(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.ANALYSIS;
  },
  isAnalysisTemplate(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.ANALYSIS_TEMPLATE;
  },
  isAnalyticsDashboard(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.ANALYTICS_DASHBOARD;
  },
  isAnalyticsQuery(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.ANALYTICS_QUERY;
  },
  supportsBatches(apiId: string): boolean {
    const type = apiIdToMaybeType(apiId);
    return type != null && BATCH_SUPPORTED_TYPES.includes(type);
  },
  withPrefixIfMissing,
  isSequenceAnalysis(apiId: string): boolean {
    // Note: sequence analyses may be either alignments or digests.
    return apiIdToMaybeType(apiId) === ModelTypeConstants.SEQUENCE_ANALYSIS;
  },
  isProteinAlignment(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.PROTEIN_ALIGNMENT;
  },
  isProcedureStepExecutionInstance(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.PROCEDURE_STEP_EXECUTION_INSTANCE;
  },
  isRequest(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.REQUEST;
  },
  isRequestV2Definition(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.REQUEST_V2_DEFINITION;
  },
  isRequestV2Submission(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.REQUEST_V2_SUBMISSION;
  },
  isRun(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.ASSAY_RUN;
  },
  isSource(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.SOURCE;
  },
  isBenchlingApp(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.BENCHLING_APP;
  },
  isTeam(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.TEAM;
  },
  isEntity(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.ENTITY;
  },
  isParty(apiId: string): boolean {
    const type = apiIdToMaybeType(apiId);
    return type != null && (PARTY_TYPES as unknown as Array<string>).includes(type);
  },
  isMolecule(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.MOLECULE;
  },
  isReaction(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.REACTION;
  },
  isTemplateCollection(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.TEMPLATE_COLLECTION;
  },
  isAnyEntryOrTemplate(apiId: string): boolean {
    const type = apiIdToMaybeType(apiId);
    return (
      type != null &&
      [
        ModelTypeConstants.ENTRY,
        ModelTypeConstants.STAGE_ENTRY,
        ModelTypeConstants.TEMPLATE,
        ModelTypeConstants.STAGE_CONFIGURATION,
      ].includes(type)
    );
  },
  isSchemaField(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.SCHEMA_FIELD;
  },
  isSchemaFieldSelectorOption(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.SCHEMA_FIELD_SELECTOR_OPTION;
  },
  isSampleDefinition(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.SAMPLE_DEFINITION;
  },
  isTestDefinition(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.TEST_DEFINITION;
  },
  isTestOrder(apiId: string): boolean {
    return apiIdToMaybeType(apiId) === ModelTypeConstants.TEST_ORDER;
  },
};

export default APIUtil;
